/**
 * Hook to block Google Funding Choices for premium users
 *
 * This hook ensures Funding Choices messages don't appear for premium users
 * by removing scripts, iframes, and modal elements.
 */
import { useEffect, useRef } from 'react';
import { useSubscription } from './useSubscription';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
export const useFundingChoicesBlocker = () => {
    const { isAdFree } = useSubscription();
    const observerRef = useRef<MutationObserver | null>(null);
    const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }
        // Function to remove Funding Choices elements
        const removeFundingChoices = () => {
            let removedCount = 0;
            // Remove Funding Choices scripts
            const scripts = document.querySelectorAll('script[src*="fundingchoicesmessages"], script[data-funding-choices]');
            scripts.forEach(script => {
                script.remove();
                removedCount++;
            });
            // Remove Funding Choices iframe
            const iframe = document.querySelector('iframe[name="googlefcPresent"]');
            if (iframe) {
                iframe.remove();
                removedCount++;
            }
            // Remove Funding Choices modal/overlay elements
            // Exclude Material-UI components by checking for MUI classes first
            const overlays = document.querySelectorAll('[id*="googlefc"], [class*="googlefc"], [id*="funding-choices"], ' +
                '[id*="fc-"], [class*="funding-choices"]');
            overlays.forEach(overlay => {
                // Skip Material-UI components
                const className = overlay.className?.toString() || '';
                if (className.includes('Mui') || 
                    className.includes('mui-') || 
                    overlay.closest('.MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiDrawer-root, .MuiPopper-root')) {
                    return; // Skip Material-UI components
                }
                
                const html = overlay.innerHTML?.toLowerCase() || '';
                const id = overlay.id?.toLowerCase() || '';
                const classLower = className.toLowerCase();
                if (html.includes('funding') ||
                    html.includes('unlock') ||
                    html.includes('view a short ad') ||
                    id.includes('googlefc') ||
                    id.includes('funding') ||
                    classLower.includes('googlefc') ||
                    classLower.includes('funding')) {
                    overlay.remove();
                    removedCount++;
                }
            });
            
            // Also check for fixed position divs, but exclude Material-UI components
            const fixedDivs = document.querySelectorAll('div[style*="position: fixed"][style*="z-index"]');
            fixedDivs.forEach(div => {
                // Skip Material-UI components
                const className = div.className?.toString() || '';
                if (className.includes('Mui') || 
                    className.includes('mui-') || 
                    div.closest('.MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiDrawer-root, .MuiPopper-root')) {
                    return; // Skip Material-UI components
                }
                
                const html = div.innerHTML?.toLowerCase() || '';
                const id = div.id?.toLowerCase() || '';
                const classLower = className.toLowerCase();
                // Only remove if it's clearly a Funding Choices element
                if ((html.includes('funding') || html.includes('googlefc') || html.includes('unlock') || html.includes('view a short ad')) &&
                    (id.includes('googlefc') || id.includes('funding') || classLower.includes('googlefc') || classLower.includes('funding'))) {
                    div.remove();
                    removedCount++;
                }
            });
            // Block window.googlefc functions for premium users
            if (isAdFree && window.googlefc) {
                // Override controlledMessagingFunction to always block
                window.googlefc.controlledMessagingFunction = function (message: any) {
                    if (message && typeof message.proceed === 'function') {
                        message.proceed(false);
                    }
                    else if (message && typeof message.dismiss === 'function') {
                        message.dismiss();
                    }
                };
                removedCount++;
            }
            if (removedCount > 0) {
                logger.debug(`🚫 Removed ${removedCount} Funding Choices elements for premium user`);
            }
            return removedCount;
        };
        // Only run for premium users
        if (!isAdFree) {
            return;
        }
        const isServerBlocked = document.documentElement.getAttribute('data-premium-user') === 'true' ||
            document.documentElement.getAttribute('data-funding-choices-blocked') === 'true' ||
            document.documentElement.getAttribute('data-k9-user') === 'true';
        if (!isServerBlocked && !isAdFree) {
            return;
        }
        // Remove Funding Choices immediately
        removeFundingChoices();
        // Set up periodic cleanup
        cleanupIntervalRef.current = setInterval(() => {
            const removed = removeFundingChoices();
            if (removed > 0) {
                logger.warn('⚠️ Found and removed Funding Choices elements that were injected');
            }
        }, 1000); // Check every second
        // Set up MutationObserver to catch dynamically injected Funding Choices
        observerRef.current = new MutationObserver((mutations) => {
            let shouldClean = false;
            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;
                            
                            // Skip Material-UI components immediately to avoid interference
                            const className = element.className?.toString() || '';
                            if (className.includes('Mui') || 
                                className.includes('mui-') || 
                                element.closest('.MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiDrawer-root, .MuiPopper-root')) {
                                return; // Skip Material-UI components
                            }
                            
                            const id = element.id?.toLowerCase() || '';
                            const classLower = className.toLowerCase();
                            const tagName = element.tagName?.toLowerCase() || '';
                            const isFundingChoices = id.includes('googlefc') ||
                                id.includes('funding') ||
                                classLower.includes('googlefc') ||
                                classLower.includes('funding') ||
                                (tagName === 'script' && ((element as HTMLScriptElement).src?.includes('fundingchoicesmessages') ||
                                    element.hasAttribute('data-funding-choices'))) ||
                                (tagName === 'iframe' && ((element as HTMLIFrameElement).name === 'googlefcPresent' ||
                                    (element as HTMLIFrameElement).src?.includes('fundingchoicesmessages')));
                            if (isFundingChoices) {
                                logger.warn('🚨 MutationObserver detected Funding Choices injection - removing immediately');
                                element.remove();
                                shouldClean = true;
                            }
                        }
                    });
                }
            }
            if (shouldClean) {
                removeFundingChoices();
            }
        });
        // Observe the document for Funding Choices injections
        if (document.body) {
            observerRef.current.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['src', 'id', 'class', 'name']
            });
        }
        // Cleanup
        return () => {
            if (cleanupIntervalRef.current) {
                clearInterval(cleanupIntervalRef.current);
                cleanupIntervalRef.current = null;
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [isAdFree]);
};
